import { forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { CrudFunctions } from '../../utilities/crud-functions';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import GlobalDialog from '../Modal/GlobalDialog';
import { Popup as DialogPopup } from 'devextreme-react/popup';

import { LoadIndicator } from 'devextreme-react';
import DataGrid, {
    FilterRow,
    HeaderFilter,
    SearchPanel,
    Export,
    Column,
    Selection,
    Toolbar,
    Item as ToolbarItem,
    FilterPanel,
    FilterBuilderPopup,
    Position
} from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { saveAs } from 'file-saver';
import { Workbook } from 'exceljs';

interface RowHistory {
    modifyUser: string;
    modifyTimestamp: string | null;
    rowPointer: string | null;
    operation: string;
    fieldName: string;
    value: string;
    enumValue: string | null;
    isCurrentRow: boolean;
}

export interface RowHistoryPopupRef {
    open(tableName: string, keyValue: string): void;
}

// This component will receive a ref from a parent component, allowing the parent to call the `open` method to show the popup and load the history for a specific row
// The way to achieve this is to wrap the function in a forwardRef which will inject the ref into the component, and then use the `useImperativeHandle` hook to expose the `open` method to the parent component
// The parent component should declare a ref and then pass it to this component, and then call the `open` method on the ref when needed; e.g.:
/*
    const rowHistoryPopupRef = useRef<RowHistoryPopupRef>(null);
    <RowHistoryPopup ref={rowHistoryPopupRef} />
    rowHistoryPopupRef.current?.open('tableName', tableId);
*/

export default forwardRef<RowHistoryPopupRef>(function RowHistoryPopup(_, ref) {
    const [visible, setVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<RowHistory[]>([]);
    const [title, setTitle] = useState('Row history');
    const [globalDialogMetadata, setGlobalDialogMetadata] = useState<ModalMetadata>();
    const [globalDialogVisible, setGlobalDialogVisible] = useState<boolean>();

    // useCallback is used to memoize the function so that it doesn't get recreated on every render
    // This prevents the ref from pointing to an old version
    const loadHistory = useCallback(async (tableName: string, keyValue: string) => {
        try {
            //throw new Error('error testing');
            const data = await CrudFunctions.postData('auditing/row-history', {
                tableName, keyValue
            });
            setRows(data);
        } catch (e) {
            setError('Unable to load history for this row');
            setRows([]);
            showMessage(e, ModalMessageType.Error, "Error Retrieving Audit History");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // The parent component can call this `open` method to show the popup and load the history for a specific row
    // e.g. popupRef.current?.open('TableName', 'KeyValue');
    useImperativeHandle(ref, () => ({
        open: (tableName: string, keyValue: string) => {
            setRows([]);
            setError(null);
            setIsLoading(true);
            setTitle(`Row History - ${tableName} - ${keyValue}`); // todo optional displayName instead of tableName? 
            setVisible(true);
            loadHistory(tableName, keyValue);
        }
    }));
    return (
        <DialogPopup
            visible={visible}
            onHiding={() => setVisible(false)}
            width={'85%'}
            height="auto"
            minHeight={'30%'}
            maxHeight={'95%'}
            showCloseButton
            dragEnabled={false}
        >
            {error && <div className="warning-text">{error}</div>}
            {!error && isLoading && <><LoadIndicator visible={isLoading} /> <p>Loading...</p></>}
            {!error && !isLoading && (
                <DataGrid
                    dataSource={rows}
                    showBorders
                    rowAlternationEnabled
                    wordWrapEnabled
                    allowColumnResizing={true}
                    onExporting={onExporting}
                    height="100%"
                >
                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <FilterBuilderPopup>
                        <Position of={window} at='center' my='center' />
                    </FilterBuilderPopup>

                    <SearchPanel visible={true}
                        width={150}
                        placeholder="Search..." />
                    <Export enabled={true} allowExportSelectedData={true} />

                    <Column dataField="modifyTimestamp" caption="Timestamp" dataType="datetime" width={160} sortOrder="asc" />
                    <Column dataField="modifyUser" caption="User" width={100} />
                    <Column dataField="operation" caption="Operation" width={90} sortOrder="asc" />
                    <Column dataField="fieldName" caption="Field" width={160} />
                    <Column dataField="value" caption="Value" />
                    <Column dataField="enumValue" caption="Display value" width={150} />
                    <Column dataField="isCurrentRow" caption="Current" dataType="boolean" width={90} />
                    <Toolbar>
                        <ToolbarItem location="before">
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{`${title}`}</div></div>
                        </ToolbarItem>
                        <ToolbarItem name="searchPanel" location='after' />
                        <ToolbarItem name="exportButton" location='after' />
                    </Toolbar>
                </DataGrid>
            )}

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </DialogPopup>
    );

    function onExporting(e: any) {
        const name = `DMA Audit Log_${new Date().toISOString().replace(/[:.]/g, '-')}`;
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('RowHistory');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${name}.xlsx`);
            });
        });

        e.cancel = true;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

});